<template>
    <v-layout fill-height>
        <v-container>
            <Loading v-if="gettingAttributeDetails"></Loading>
            <v-row v-else justify="center">
                <v-col>
                    <v-data-table
                        id="attValuesTable"
                        :headers="attValueHeaders"
                        :items="attribute.values"
                        :items-per-page="5"
                        item-key="assetListingId"
                        class="elevation-2"
                        dense
                    >   
                        <template v-slot:item.sort="">
                            <v-btn icon class="pa-0 ma-0 mr-1 handler">
                                <v-icon size="18px" dark>mdi-drag-horizontal-variant</v-icon>
                            </v-btn>
                        </template>

                        <template v-slot:item.thumbnail="{ item }">
                            <v-card flat 
                                v-if="item.thumbnail != null" 
                                class="pa-2"
                            >
                                <v-img :src="item.thumbnail" contain style="height:80px;width:100%"></v-img>
                            </v-card>
                        </template>

                        <template v-slot:item.action="{ item }">
                            <v-btn icon class="pa-0 ma-0">
                                <v-icon size="18px" dark v-on:click="showDeletePrompt = true; attValueToDelete = item">mdi-delete</v-icon>
                            </v-btn>
                            <v-btn icon class="pa-0 ma-0">
                                <v-icon size="18px" dark v-on:click="showImageUploadPrompt = true; assetToUploadTo = item">mdi-image-edit</v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-container>

        <v-dialog
            v-model="showDeletePrompt"
            width="500px"
            height="200px"
            v-if="CanUserEditAssets()"
            persistent
        >
            <v-card>
                <v-card-title class="pa-3 headline">Delete Attribute Value</v-card-title>

                <v-card-text class="cardcontent">
                    <v-row class="subtitle-1">
                        <span>Are you sure you want to delete this attribute value?</span>
                    </v-row>
                    <v-row>
                        Deleting this attribute value will remove it from all assets.
                    </v-row>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        style="width:100px"
                        color="green darken-1"
                        dark
                        @click="showDeletePrompt = false"
                    >
                        Cancel
                    </v-btn>

                    <v-btn
                        style="width:150px"
                        color="red darken-1"
                        dark
                        @click="deleteAttributeValue"
                    >
                        <v-icon 
                            v-if="this.deletingAttributeValue" 
                            size="28" 
                            color="white" dark
                        >
                            mdi-loading mdi-spin
                        </v-icon>
                        <span v-else>Confirm</span>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <v-dialog
            v-model="showImageUploadPrompt"
            width="500px"
            height="200px"
            v-if="CanUserEditAssets()"
            persistent
        >
            <v-card>
                <v-card-title class="pa-3 headline">Upload Thumbnail</v-card-title>

                <v-card-text class="cardcontent">
                    <v-row class="cardcontent">
                        <v-col cols="4" class="pa-1" justify="center" align="center">
                            <v-card height="190px" class="justify-center">
                                <v-img max-height="190px" contain :src='(this.files.length) ? this.files[0].thumb : ""'/>
                            </v-card>
                        </v-col>
                        <v-col cols="8" class="pa-1" >
                            <v-card
                                class="upload"
                                color="transparent"
                                min-height="200px"
                                flat
                            >
                                <v-simple-table v-if="this.files.length">
                                    <template v-slot:default>
                                        <thead>
                                            <tr>
                                                <th class="text-left">File Name</th>
                                                <th class="text-left"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(file,index) in files" :key="file.id">
                                                <td>{{ file.name }}</td>
                                                <td>
                                                    <v-btn class="icon-btn" color="red" icon @click="files.splice(index,1)">
                                                        <v-icon>mdi-file-remove</v-icon>
                                                    </v-btn>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                                
                                <v-container 
                                    class="fill-height pa-4"
                                    fluid
                                > 
                                    <v-row justify="center" align="center">
                                        <v-btn max-width="220px">
                                            <label for="file" class="btn btn-lg btn-primary" style="">
                                                Drop or Select Files
                                            </label>
                                        </v-btn>
                                    </v-row>
                                </v-container>
                            </v-card>
                        </v-col>
                    </v-row>
                    <file-upload
                        :multiple="true"
                        :drop="true"
                        :drop-directory="true"
                        :maximum="1"
                        accept="image/*"
                        @input-filter="inputFilter"
                        v-model="files"
                        ref="upload"
                        :headers="GetAuthHeaderObj()"
                    ></file-upload>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        style="width:100px"
                        color="green darken-1"
                        dark
                        @click="showImageUploadPrompt = false"
                    >
                        Cancel
                    </v-btn>

                    <v-btn
                        style="width:150px"
                        color="red darken-1"
                        dark
                        @click="uploadAttValueImage"
                    >
                        <v-icon 
                            v-if="this.uploadingImages" 
                            size="28" 
                            color="white" dark
                        >
                            mdi-loading mdi-spin
                        </v-icon>
                        <span v-else>Upload</span>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-layout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Sortable from "sortablejs"
import FileUpload from 'vue-upload-component'
import Loading from '../../shared/Loading'

export default {
    name: "AttributeBindingConditions",
    components: {
        FileUpload,
        Loading
    },
    props: {
        inputAttribute: {
            default: () =>  ({}),
            type: Object
        }
    },
    computed:{
    },
    mounted:  function () {
        if(this.inputAttribute.assetAttributeId != null)
        {
            this.attribute = this.inputAttribute;
            this.getAttributeValues();
        }
        
        var table = document.querySelector("#attValuesTable tbody");
        const _self = this;

        Sortable.create(table, {
            handle: ".handler",
            onEnd({ newIndex, oldIndex }) {
                const rowSelected = _self.attribute.values.splice(oldIndex,1)[0];
                _self.attribute.values.splice(newIndex, 0, rowSelected);
                _self.saveOrder();
            }
        });
    },
    data: () => ({
        orderingAttributes: false,
        showDeletePrompt: false,
        showImageUploadPrompt: false,
        uploadingImages: false,
        assetToUploadTo: null,
        toggleDefault: true,
        files: [],
        file : {},
        deletingAttributeValue: false,
        gettingAttributeDetails: false,
        attValueToDelete: {},
        attribute: {},
        attValueHeaders : [
            { text: '', value: 'sort', sortable: false, align: 'center', width: '5%' },
            { text: '', value: 'thumbnail', sortable: false, align: 'center',  width: '10%' },
            { text: 'Value', value: 'name' },
            { text: 'Assets With Value', value: 'assetCount',},
            { text: '', value: 'action', sortable: false, align: 'center',  width: '10%' },
        ]
    }),
    events: {
    },
    methods: {
        ...mapGetters("authentication", [
            "CanUserEditAssets",
            "GetAuthHeaderObj"
        ]),
        ...mapActions("assetxpstate", [
            "RemoveAsset",
            "OrderAttributeValues",
            "UploadResources",
            "GetAttributeDetails"
        ]),

        saveOrder() {
            this.orderingAttributes = true;

            this.OrderAttributeValues(this.attribute).then((response) =>{
                this.orderingAttributes = false;
            });
        },
        async uploadAttValueImage () {
            this.uploadingImages = true; 
            const index = this.attribute.values.indexOf(this.assetToUploadTo)

            let formData = new window.FormData();
            formData.set('assetDetailId', this.assetToUploadTo.assetDetailId);
            formData.set('toggleDefault', this.toggleDefault);

            for (var i = 0; i < this.$refs.upload.files.length; i++) {
                var file = this.$refs.upload.files[i];
                let value

                for (let key in file.data) {
                value = file.data[key]
                if (value && typeof value === 'object' && typeof value.toString !== 'function') {
                    if (value instanceof File) {
                    formData.append(key, value, value.name)
                    } else {
                    formData.append(key, JSON.stringify(value))
                    }
                } else if (value !== null && value !== undefined) {
                    formData.append(key, value)
                }
                }

                formData.append(this.name, file.file, file.file.filename || file.name)
            }

            this.UploadResources(formData).then((response) =>{
                this.uploadingImages = false;
                this.showImageUploadPrompt = false;
                this.attribute.values[index].thumbnail = response.data.thmUrl;
                this.files = [];
                this.file = null;
            });
        },
        updatedBinding() {
            this.$emit("AttributeUpdated", null)
        },
        deleteAttributeValue () {
            this.deletingAttributeValue = true;
            const index = this.attribute.values.indexOf(this.attValueToDelete)

            this.RemoveAsset(this.attValueToDelete.assetDetailId).then( () =>{
                this.attValueToDelete = null;
                this.showDeletePrompt = false;
                this.deletingAttributeValue = false;
                this.attribute.values.splice(index, 1);
            });
        },
        getAttributeValues() {
            this.gettingAttributeDetails = true;
            this.GetAttributeDetails(this.attribute.assetAttributeId).then( (result) => {
                this.attribute = result.data;
                this.gettingAttributeDetails = false;
            });
        },
        inputFilter(newFile, oldFile, prevent) {
            if (newFile && !oldFile) {
                // Before adding a file
                // Filter system files or hide files
                if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
                    return prevent()
                }

                // Filter php html js file
                if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
                    return prevent()
                }
            }

            if (newFile && (!oldFile || newFile.file !== oldFile.file)) {
                // Create a blob field
                newFile.blob = ''
                let URL = window.URL || window.webkitURL
                
                if (URL && URL.createObjectURL) {
                    newFile.blob = URL.createObjectURL(newFile.file)
                }

                // Thumbnails
                newFile.thumb = ''
                
                if (newFile.blob && newFile.type.substr(0, 6) === 'image/') {
                    newFile.thumb = newFile.blob
                }
            }
        }
    },
    watch: {
        inputAttribute() {
            if(this.inputAttribute.assetAttributeId != null)
            {
                this.attribute = this.inputAttribute;
            }
        }
    }
}
</script> 

<style>
.handler {
    cursor: move !important;
    cursor: -webkit-grabbing !important;
}
</style>